/* assets/product-upsell-recommendations.js
 *
 * Lightweight custom element that replaces <product-recommendations>
 * for the PDP upsell block. It fetches S&D complementary data once,
 * finds the correct block in the response, inserts it, and then
 * becomes inert (never re-fetches or re-replaces).
 *
 * This solves the core issue: the generic ProductRecommendations element
 * picks up the FIRST <product-recommendations> in the response HTML
 * (often the complementary block) and replaces the upsell content with
 * the wrong markup, destroying the initialized upsell slider.
 */
(function () {
  "use strict";

  if (customElements.get("product-upsell-recommendations")) return;

  class ProductUpsellRecommendations extends HTMLElement {
    constructor() {
      super();
      this._loaded = false;
    }

    connectedCallback() {
      // Only fetch once; if already loaded (e.g. element re-attached), skip.
      if (this._loaded) return;

      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(this);
          this._fetchAndInsert();
        },
        { rootMargin: "0px 0px 400px 0px" }
      );

      observer.observe(this);
    }

    async _fetchAndInsert() {
      const url = this.dataset.url;
      if (!url) {
        console.warn("[ProductUpsellRecommendations] Missing data-url");
        return;
      }

      try {
        const res = await fetch(url);
        const text = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");

        // Strategy: find the correct block in the response.
        // We use the block ID from shopify_attributes to find our exact element.
        const blockId = this.dataset.blockId;
        let sourceEl = null;

        if (blockId) {
          // Shopify adds data-shopify-editor-block as block attributes.
          // But more reliably, we can look for our own custom element or
          // the upsell-specific markup inside the matching product-recommendations.
          const allRecs = doc.querySelectorAll("product-upsell-recommendations, product-recommendations");

          for (const rec of allRecs) {
            // Check if this element contains our upsell root
            if (rec.querySelector("[data-upsell-root]")) {
              sourceEl = rec;
              break;
            }
          }
        }

        // Fallback: look for any element containing our upsell root
        if (!sourceEl) {
          sourceEl = doc.querySelector("[data-upsell-root]")?.closest(
            "product-upsell-recommendations, product-recommendations"
          );
        }

        // Final fallback: just look for the upsell outer div directly
        if (!sourceEl) {
          const upsellOuter = doc.querySelector("[data-upsell-outer]");
          if (upsellOuter) {
            sourceEl = { innerHTML: upsellOuter.outerHTML };
          }
        }

        if (sourceEl && sourceEl.innerHTML.trim().length) {
          this.innerHTML = sourceEl.innerHTML;
          this._loaded = true;

          // Give the browser a tick to connect the new custom elements,
          // then explicitly initialize the upsell slider.
          requestAnimationFrame(() => {
            const slider = this.querySelector("cart-popular-products-slider");
            if (slider && typeof slider.init === "function") {
              // Cancel the load listener to prevent double init.
              // The slider's connectedCallback added a load listener, but
              // we're calling init() explicitly here — the load event would
              // trigger a second init that destroys/recreates Swiper and
              // resets select states.
              if (slider._onLoadInit) {
                window.removeEventListener("load", slider._onLoadInit);
              }
              slider.init();
            }
          });
        } else {
          // No upsell content in the response — hide the block
          this.setAttribute("hidden", "");
        }
      } catch (err) {
        console.error("[ProductUpsellRecommendations] Fetch failed:", err);
      }
    }
  }

  customElements.define(
    "product-upsell-recommendations",
    ProductUpsellRecommendations
  );
})();